import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material'
import { useQuery } from 'react-query'
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import * as React from 'react'
import { useState } from 'react'
import { Undo } from '@mui/icons-material'
import { SpreadsheetStatus } from './DataEntry'
import useInterval from '../../../Functions/SourcedOnline/useInterval'
import SpreadsheetService from '../../../APIs/Python/APIs/SpreadsheetService'
import { useUserContext } from '../../../Contexts/UserConfig'
import { useMessagePopupContext } from '../../../garvan-react/Components/ModalFeedback/MessagePopup'
import { AxiosResponse } from 'axios'
import PageSection from '../../../Constants&Theme/PageSection'

function DataExport() {
  const { whoAmI } = useUserContext()
  const { study_id } = useParams()
  const { setError } = useMessagePopupContext()

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')

  const {
    data: spreadsheet,
    refetch,
    error,
  } = useQuery<SpreadsheetStatus, AxiosResponse>(
    ['SpreadsheetService', 'checkSpreadsheet', study_id, id],
    () => SpreadsheetService.checkSpreadsheet(whoAmI, study_id, id!),
    {
      enabled: !!whoAmI && !!study_id && !!id,
      onError: (err) => setError(err),
    },
  )

  const maxPolls = 30
  const secondInterval = 2
  const [pollsRemain, setPollsRemain] = useState(maxPolls)

  useInterval(async () => {
    if (
      !error &&
      id &&
      !spreadsheet?.URL &&
      pollsRemain > 0 &&
      spreadsheet?.Status !== 'ERR'
    ) {
      setPollsRemain((t) => t - 1)
      await refetch()
    }
  }, secondInterval * 1000) // to seconds

  if (location.pathname.endsWith('/export') && !id) {
    return (
      <Navigate
        to={`../`}
        replace
      />
    )
  }
  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mx: 2,
      }}
    >
      <PageSection
        title={'Export Study Data'}
        loading={pollsRemain > 0 && spreadsheet?.Status !== 'ERR'}
        leftDecorator={
          <Button
            onClick={() => {
              if (location.state?.source === 'Animal') {
                navigate('../entry/select')
              } else if (location.state?.source === 'Study') {
                navigate('../../')
              } else {
                navigate(-1)
              }
            }}
            color={'info'}
            variant={'contained'}
            startIcon={<Undo />}
          >
            {location.state?.source === 'AnimalSelection' ?
              'Return to Animal Selection'
            : 'Return to study selection'}
          </Button>
        }
      >
        <Box sx={{ width: 1, display: 'flex' }}>
          {spreadsheet?.URL && (
            <Box sx={{ flex: 1, justifyContent: 'center', display: 'flex' }}>
              <Alert
                severity={'info'}
                sx={{ display: 'inline-flex' }}
              >
                <AlertTitle>
                  <strong>Export File</strong>
                </AlertTitle>
                {'[Spreadsheet Menu] File > Download > [Select Format]'}
              </Alert>
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
        </Box>
        {!spreadsheet?.URL && (
          <Box sx={{ mt: 2, width: 1, maxWidth: 'md' }}>
            {pollsRemain === 0 && spreadsheet?.Status !== 'ERR' && (
              <Alert severity={'error'}>
                <Box
                  sx={{
                    minWidth: 160,
                    width: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {'Timeout:'}
                    </Typography>
                    &nbsp;&nbsp;
                    <Typography>{`The spreadsheet is taking too long to load. The UI has reached its polling limit of ${maxPolls * secondInterval}s`}</Typography>
                  </Box>
                </Box>
              </Alert>
            )}
            {spreadsheet?.Status === 'ERR' && (
              <Alert severity={'error'}>
                <Box
                  sx={{
                    minWidth: 160,
                    width: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {'Error:'}
                    </Typography>
                    &nbsp;&nbsp;
                    <Typography>{spreadsheet?.StatusMessage}</Typography>
                  </Box>
                </Box>
              </Alert>
            )}
          </Box>
        )}
        {/*https://github.com/gatsbyjs/gatsby/issues/14872 issue requires a fix*/}
        {spreadsheet?.URL && (
          <Box sx={{ m: 1, width: 1 }}>
            <iframe
              title={'DataEntryGoogleSheet'}
              src={`https://docs.google.com/spreadsheets/d/${spreadsheet.URL}/edit#gid=0?rm=minimal`}
              width="100%"
              height="800"
            />
          </Box>
        )}
      </PageSection>
    </Box>
  )
}

export default DataExport
